import { ICartItem } from "@/services/api/CartItem/type";
import cartApi from "../../../services/api/Cart";
import cartItemApi from "../../../services/api/CartItem";
import { CURRENT_SUBDOMAIN, DEMANDORIA_SUBDOMAINS } from "@/utils/stringUtils";
import { TFunction } from "i18next";
import orderApi from "@/services/api/Order";
import cardTokenApi from "@/services/api/CardToken";
import userGatewayApi from "@/services/api/UserGateway";
import { type IUser } from "@/services/api/User/types";
import { CreditCardData, FraudData, PaymentMethod } from "./types";
export type { CreditCardData, PaymentMethod };

export const handleRemoveCartItem = async (itemId: string, t: TFunction) => {
  const isSuitcase = CURRENT_SUBDOMAIN === DEMANDORIA_SUBDOMAINS[2];

  const confirmMessage = t(
      isSuitcase
          ? "shoppingCart.alerts.removeItem_suitcase"
          : "shoppingCart.alerts.removeItem_cart"
  );

  if (confirm(confirmMessage)) {
    try {
      await cartItemApi.deleteCartItem(itemId);
    } catch (error) {
      console.error("Error removing cart item", error);
    }
  }
};

export const handleClearCartClick = async (cartId: string, t: TFunction) => {
  if (confirm(t("shoppingCart.alerts.clearCart"))) {
    try {
      await cartApi.deleteCart(cartId);
    } catch (error) {
      console.warn("Error cleaning items from cart: ", error);
    }
  }
};

export const handleRemoveAllCartItems = async (items: ICartItem[], t: TFunction) => {
  if (confirm(t("shoppingCart.alerts.clearCart"))) {
    try {
      await Promise.all(items.map((item) => cartItemApi.deleteCartItem(item.id)));
    } catch (error) {
      console.error("Error removing cart item", error);
    }
  }
};

const CARD_PAYMENT_METHODS: PaymentMethod[] = [
  "CREDIT_CARD",
  "CREDIT_CARD_EARLY_BUYER",
  "DEBIT_CARD",
];

function onlyDigits(v: string): string {
  return v.replace(/\D/g, '');
}

function convertExpiration(exp: string): string {
  const digits = exp.replace(/\D/g, '');
  if (digits.length !== 4) return exp;
  return digits.slice(0, 2) + '/20' + digits.slice(2);
}

function buildFraudData(user: IUser): FraudData | null {
  if (!user.docNumber || !user.name || !user.email) return null;

  const addressFields = {
    country: user.country || "BR",
    state: user.state || "",
    city: user.city || "",
    district: user.district || "",
    street: user.logradouro || "",
    zipCode: user.CEP ? onlyDigits(user.CEP) : "",
    number: user.number || "",
  };

  const hasAddress = addressFields.state && addressFields.city && addressFields.street && addressFields.zipCode && addressFields.number;

  return {
    document: onlyDigits(user.docNumber),
    name: user.name,
    email: user.email,
    phone: user.phone ? onlyDigits(user.phone) : "",
    ...(hasAddress && { billingAddress: addressFields }),
  };
}

export const handleCheckout = async (
    cartId: string,
    total: number,
    t: TFunction,
    paymentMethod: PaymentMethod,
    card: CreditCardData | null,
    user: IUser,
    onSuccess: (order: any) => void,
    onError: (msg: string) => void,
) => {
  try {
    const fraudData = buildFraudData(user);

    const payload: any = {
      cartId,
      value: total,
      title: "Pedido via carrinho",
      installments: card?.totalParcelas ?? 1,
      payment: {
        method: paymentMethod,
        softDescriptor: "Pedido",
      },
    };

    if (fraudData) {
      payload.payment.fraudData = fraudData;
    }

    const doesPaymentMethodUseCard = CARD_PAYMENT_METHODS.includes(paymentMethod);

    if (doesPaymentMethodUseCard && card) {
      const userGateway = await userGatewayApi.getOrCreateMine();

      if (!userGateway) {
        onError(t("shoppingCart.alerts.noPaymentRegistration"));
        return;
      }

      const tokenResp = await cardTokenApi.create({
        tokenize: {
          holderName: card.holderName,
          number: onlyDigits(card.number),
          cvv: card.cvv,
          expiration: convertExpiration(card.expiration),
          buyerUuid: userGateway.buyerId,
        },
        userGatewayId: userGateway.id,
      });

      payload.payment.card = {
        cardToken: tokenResp.tokenUuid,
        cvv: card.cvv,
      };
    }

    const order = await orderApi.create(payload);

    const syncedOrder = await orderApi.syncPayment(order.id);

    if (paymentMethod !== "PIX") {
      await cartApi.finishCart(cartId);
    }

    onSuccess(syncedOrder);
  } catch (error: any) {
    const msg =
        error?.response?.data?.message ||
        t("shoppingCart.alerts.paymentError");

    onError(msg);
  }
};